import { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Redirect, Route, Switch } from "react-router";
import { PathName } from "../../../constants/PathName";
import { UserContext } from "../../../contexts/UserContext";
import { FormBuilderFormEdit } from "./FormBuilderFormEdit";
import { FormBuilderSelectEdit } from "./FormBuilderSelectEdit";
import { FormBuilderTabs } from "./FormBuilderTabs";
import { FormsTable } from "./FormsTable";
import { SelectsTable } from "./SelectsTable";
import "./formBuilder.scss";

export const FormBuilder = () => {
	const { store } = useContext(UserContext) as { store: { isSuperUser: boolean } };
	const [showBackToTop, setShowBackToTop] = useState(false);

	// Enable scrolling on form builder pages
	useEffect(() => {
		document.body.classList.add("form-builder-page");
		return () => {
			document.body.classList.remove("form-builder-page");
		};
	}, []);

	// Handle scroll to show/hide back to top button
	useEffect(() => {
		const handleScroll = () => {
			// Check both window and body scroll since body might be the scrolling container
			const scrollTop =
				window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
			setShowBackToTop(scrollTop > 300);
		};

		// Listen to scroll on both window and body
		window.addEventListener("scroll", handleScroll, true);
		document.body.addEventListener("scroll", handleScroll, true);

		// Initial check
		handleScroll();

		return () => {
			window.removeEventListener("scroll", handleScroll, true);
			document.body.removeEventListener("scroll", handleScroll, true);
		};
	}, []);

	const scrollToTop = () => {
		// Scroll both window and body to ensure it works regardless of which is scrolling
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
		document.body.scrollTo({
			top: 0,
			behavior: "smooth",
		});
		document.documentElement.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	};

	if (!store.isSuperUser) {
		return <Redirect to={PathName.DASHBOARD} />;
	}

	return (
		<div className="formBuilder">
			<div className="formBuilder-header">
				<h1>Form Builder</h1>
				<p>Create and manage forms and select lists.</p>
			</div>
			<div className="row actions">
				<div className="col-xs-12 col-sm-8">
					<FormBuilderTabs />
				</div>
			</div>
			<div className="formBuilder-content">
				<Switch>
					<Route
						exact
						path={PathName.FORM_BUILDER}
						render={() => <Redirect to={PathName.FORM_BUILDER_FORMS} />}
					/>
					<Route exact path={PathName.FORM_BUILDER_FORMS} render={() => <FormsTable />} />
					<Route
						exact
						path={PathName.FORM_BUILDER_FORMS_ADD}
						render={() => <FormBuilderFormEdit />}
					/>
					<Route
						exact
						path={PathName.FORM_BUILDER_FORMS_EDIT()}
						render={() => <FormBuilderFormEdit />}
					/>
					<Route exact path={PathName.FORM_BUILDER_SELECTS} render={() => <SelectsTable />} />
					<Route
						exact
						path={PathName.FORM_BUILDER_SELECTS_ADD}
						render={() => <FormBuilderSelectEdit />}
					/>
					<Route
						exact
						path={PathName.FORM_BUILDER_SELECTS_EDIT()}
						render={() => <FormBuilderSelectEdit />}
					/>
					<Route render={() => <Redirect to={PathName.FORM_BUILDER_FORMS} />} />
				</Switch>
			</div>
			{createPortal(
				<button
					type="button"
					className={`formBuilder-backToTop ${
						showBackToTop ? "formBuilder-backToTop--visible" : ""
					}`}
					onClick={scrollToTop}
					aria-label="Back to top"
					style={{ display: showBackToTop ? "flex" : "none" }}
				>
					<img src="/assets/icon/back-to-top.png" alt="Back to top" />
				</button>,
				document.body,
			)}
		</div>
	);
};
